import { create } from 'zustand';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import type { ApiResponse, StrategyResearchQueueItem } from '@/types';
import { useAuthStore } from '@/stores/auth';

interface ResearchQueueState {
  items: StrategyResearchQueueItem[];
  loading: boolean;
  fetchQueue: () => Promise<void>;
}

export const RESEARCH_FINISHED_EVENT = 'marketing-strategy-research-finished';

let prevRunningIds = new Set<string>();
let prevStrategyByRun = new Map<string, string>();
let queuePollBootstrapped = false;
let lastUserId: string | null = null;

function dispatchFinished(
  strategyId: string,
  runId: string,
  status: 'completed' | 'failed' | 'cancelled'
) {
  window.dispatchEvent(
    new CustomEvent(RESEARCH_FINISHED_EVENT, { detail: { strategyId, runId, status } })
  );
}

export const useResearchQueueStore = create<ResearchQueueState>((set) => ({
  items: [],
  loading: false,

  fetchQueue: async () => {
    const userId = useAuthStore.getState().user?.id ?? null;
    if (!userId) {
      prevRunningIds = new Set();
      prevStrategyByRun = new Map();
      queuePollBootstrapped = false;
      lastUserId = null;
      set({ items: [], loading: false });
      return;
    }

    if (lastUserId !== userId) {
      prevRunningIds = new Set();
      prevStrategyByRun = new Map();
      queuePollBootstrapped = false;
      lastUserId = userId;
    }

    set({ loading: true });
    try {
      const res = await api.get<ApiResponse<StrategyResearchQueueItem[]>>('/marketing-strategies/research/queue');
      const items = Array.isArray(res.data) ? res.data : [];

      const currentIds = new Set(items.map((i) => i.id));
      const currentMeta = new Map(items.map((i) => [i.id, i.strategy_id] as const));

      if (queuePollBootstrapped) {
        for (const id of prevRunningIds) {
          if (currentIds.has(id)) continue;
          const strategyId = prevStrategyByRun.get(id);
          if (!strategyId) continue;
          try {
            const st = await api.get<ApiResponse<{ status: string }>>(
              `/marketing-strategies/${strategyId}/research/${id}/status`
            );
            const status = st.data?.status;
            if (status === 'completed') {
              toast.success('Research abgeschlossen!');
              dispatchFinished(strategyId, id, 'completed');
            } else if (status === 'failed') {
              toast.error('Research fehlgeschlagen.');
              dispatchFinished(strategyId, id, 'failed');
            } else if (status === 'cancelled') {
              toast.info('Research wurde abgebrochen.');
              dispatchFinished(strategyId, id, 'cancelled');
            }
          } catch {
            // ignore
          }
        }
      }

      prevRunningIds = currentIds;
      prevStrategyByRun = currentMeta;
      queuePollBootstrapped = true;

      set({ items });
    } catch {
      set({ items: [] });
    } finally {
      set({ loading: false });
    }
  },
}));
